/**
 * coachOpener — chiama il coach all'avvio di una chat con un trigger nascosto
 * "fammi il punto della situazione" e restituisce i messaggi iniziali pronti
 * per setMessages(). I messaggi con `hidden:true` non vengono renderizzati ma
 * vengono inclusi nel payload verso il backend (servono al contesto del coach).
 */
import { sendCoach, sendCoachStream } from './api';

const PERIODO_LABEL = {
  setup: 'anno',
  trimestre: 'trimestre',
  mese: 'mese',
  settimana: 'settimana',
};

export function buildTriggerMessage(momento) {
  const periodo = PERIODO_LABEL[momento] || 'periodo';
  const content =
    `[avvio] Prima di pianificare, fammi il punto della situazione: dove sono ` +
    `rispetto agli obiettivi e ai target dell'anno, cosa è a rischio o in ritardo, ` +
    `e su cosa conviene concentrarmi in questo ${periodo}. Sii sintetico ` +
    `(numeri concreti, non elenchi lunghi), poi guidami con la prima domanda.`;
  return { role: 'user', content, hidden: true };
}

/**
 * Costruisce il fallback statico (usato se la chiamata al coach fallisce).
 */
export function fallbackGreeting(momento, stato) {
  if (momento === 'setup') {
    return stato?.anno?.tema
      ? `Riprendiamo. Il tema per il ${stato.anno.anno} è: "${stato.anno.tema}". Vuoi confermarlo o cambiarlo?`
      : `Pronto per il setup del ${stato?.anno?.anno ?? 'nuovo anno'}. Iniziamo dal tema dell'anno: dimmi cosa cambia quest'anno, e perché.`;
  }
  if (momento === 'trimestre') {
    return `Pianificazione trimestrale — ${stato?.quarter_label ?? 'quarter corrente'}. Iniziamo dal consuntivo del ${stato?.quarter_precedente ?? 'quarter precedente'} o passiamo subito all'apertura del nuovo?`;
  }
  if (momento === 'mese') {
    return `Pianifichiamo ${stato?.mese_nome ?? 'il mese'}. Ho i target mensili derivati dai quarter. Cosa vuoi muovere per primo?`;
  }
  return `Review della settimana ${stato?.settimana ?? ''}. Da dove partiamo: reali dei KR, MIT chiuse o retrospettiva?`;
}

/**
 * Variante streaming: emette delta in tempo reale via onDelta(text). Ritorna lo
 * stesso shape di openWithSummary. In caso di errore stream, ricade su quello
 * non-streaming. In caso di errore anche lì, fallback statico.
 */
export async function openWithSummaryStream(momento, stato, { onDelta } = {}) {
  const trigger = buildTriggerMessage(momento);
  try {
    let accMessaggio = '';
    let finalOps = [];
    let finalSugg = [];
    await sendCoachStream({
      momento,
      stato,
      messaggi: [{ role: 'user', content: trigger.content }],
      onDelta: (delta) => {
        accMessaggio += delta;
        if (onDelta) onDelta(delta);
      },
      onDone: ({ messaggio, operazioni, suggerimenti }) => {
        if (messaggio) accMessaggio = messaggio;
        finalOps = operazioni || [];
        finalSugg = suggerimenti || [];
      },
    });
    return {
      trigger,
      assistant: {
        role: 'assistant',
        content: accMessaggio || fallbackGreeting(momento, stato),
        operazioni: finalOps.length > 0 ? finalOps : null,
        suggerimenti: finalSugg,
      },
    };
  } catch (streamErr) {
    console.warn('[coachOpener] stream fallback:', streamErr?.message);
    return openWithSummary(momento, stato);
  }
}

/**
 * Chiama il coach con il trigger e ritorna il messaggio iniziale.
 * In caso di errore, ritorna un messaggio fallback statico.
 */
export async function openWithSummary(momento, stato) {
  const trigger = buildTriggerMessage(momento);
  try {
    const res = await sendCoach({
      momento,
      stato,
      messaggi: [{ role: 'user', content: trigger.content }],
    });
    return {
      trigger,
      assistant: {
        role: 'assistant',
        content: res.messaggio || fallbackGreeting(momento, stato),
        operazioni: res.operazioni && res.operazioni.length > 0 ? res.operazioni : null,
        suggerimenti: Array.isArray(res.suggerimenti) ? res.suggerimenti : [],
      },
    };
  } catch (e) {
    console.error('[coachOpener] fallback su greeting statico:', e);
    return {
      trigger,
      assistant: { role: 'assistant', content: fallbackGreeting(momento, stato) },
    };
  }
}
